import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import { IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';

interface ISubmitTinRequest {
  tinNumber: string;
}

interface ISubmitTinResponse {
  success?: boolean;
  message?: string;
  data?: {
    id?: string;
    businessId?: string;
    userId?: string;
    cacNumber?: string | null;
    tinNumber?: string | null;
    businessType?: string;
    approvalStatus?: string;
    tinVerificationStatus?: string;
    updatedAt?: string;
    createdAt?: string;
  };
  errors?: IApiResponseError[];
}

const initialState: IApiState<ISubmitTinResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const submitTinAsync = createAsyncThunk(
  'kyb/tin/submit',
  async (values: ISubmitTinRequest, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;
      const businessId = localStorage.getItem('businessId');

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
          'X-Business-Id': businessId,
        },
      };

      const { data } = await api.put(
        `kyb/${businessId}/tin`,
        values,
        config
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<ISubmitTinResponse>;

      return rejectWithValue(error.response?.data);
    }
  }
);

export const submitTinSlice = createSlice({
  name: 'kyb/tin/submit',
  initialState,
  reducers: {
    resetSubmitTin: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitTinAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(submitTinAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as ISubmitTinResponse;
      })
      .addCase(submitTinAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as ISubmitTinResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetSubmitTin } = submitTinSlice.actions;

export default submitTinSlice.reducer;
